interface TiltEl extends HTMLElement {
  _tiltCleanup?: () => void
  _tiltDisposed?: boolean
}

// v-tilt — rotates the element in 3D based on cursor position. Pass a number to
// override the max angle in degrees (default 8). Parent needs `perspective: …`
// for the rotation to actually look 3D. A glare pseudo-child follows the cursor.
//
// Registered universally (not `.client`) so SSR can resolve the directive — the
// effect itself is client-only because `mounted` never runs on the server, and
// `getSSRProps` is a no-op.
//
// GSAP is imported on first use rather than at the top of this file. The
// directive has to be registered globally, but a static import would put GSAP
// in the main bundle of every page; only landing sections use `v-tilt`, so
// pages without one never download it.
export default defineNuxtPlugin((nuxtApp) => {
  nuxtApp.vueApp.directive('tilt', {
    getSSRProps() {
      return {}
    },
    mounted(el: TiltEl, binding) {
      if (window.matchMedia('(hover: none)').matches) return

      el._tiltDisposed = false

      import('gsap').then(({ gsap }) => {
        // The element may have been removed while GSAP was loading.
        if (el._tiltDisposed) return

        const max = typeof binding.value === 'number' ? binding.value : 8
        const rxTo = gsap.quickTo(el, 'rotationX', { duration: 0.5, ease: 'power3.out' })
        const ryTo = gsap.quickTo(el, 'rotationY', { duration: 0.5, ease: 'power3.out' })

        gsap.set(el, { transformPerspective: 800, transformStyle: 'preserve-3d' })

        const glare = document.createElement('span')
        glare.className = 'tilt-glare'
        el.appendChild(glare)

        const onMove = (e: MouseEvent) => {
          const rect = el.getBoundingClientRect()
          const px = (e.clientX - rect.left) / rect.width
          const py = (e.clientY - rect.top) / rect.height
          ryTo((px - 0.5) * 2 * max)
          rxTo(-(py - 0.5) * 2 * max)
          glare.style.setProperty('--gx', `${px * 100}%`)
          glare.style.setProperty('--gy', `${py * 100}%`)
          glare.style.opacity = '1'
        }
        const onLeave = () => {
          rxTo(0)
          ryTo(0)
          glare.style.opacity = '0'
        }

        el.addEventListener('mousemove', onMove)
        el.addEventListener('mouseleave', onLeave)
        el._tiltCleanup = () => {
          el.removeEventListener('mousemove', onMove)
          el.removeEventListener('mouseleave', onLeave)
          glare.remove()
        }
      })
    },
    unmounted(el: TiltEl) {
      el._tiltDisposed = true
      el._tiltCleanup?.()
    },
  })
})
